import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Loader from './components/Loader';
import AppLayout from './layouts/AppLayout.jsx';
import { useAuth, routeForStep } from './store/auth';

import OnboardingShell from './pages/onboarding/OnboardingShell.jsx';
import OnboardingVerifyEmail from './pages/onboarding/VerifyEmail.jsx';
import OnboardingPendingApproval from './pages/onboarding/PendingApproval.jsx';
import OnboardingPlaceholder from './pages/onboarding/StepPlaceholder.jsx';
import OnboardingAcceptTerms from './pages/onboarding/AcceptTerms.jsx';
import OnboardingTraining from './pages/onboarding/Training.jsx';
import OnboardingWelcome from './pages/onboarding/Welcome.jsx';

import Login from './pages/Login.jsx';
import Signup from './pages/Signup.jsx';
import ForgotPasswordPage from './pages/ForgotPassword.jsx';
import Landing from './pages/Landing.jsx';

import AdminDashboard from './pages/admin/Dashboard.jsx';
import Approvals from './pages/admin/Approvals.jsx';
import Users from './pages/admin/Users.jsx';
import UserProfile from './pages/admin/UserProfile.jsx';
import Permissions from './pages/admin/Permissions.jsx';
import AdminDownloads from './pages/admin/Downloads.jsx';
import AdminIncentives from './pages/admin/Incentives.jsx';
import AdminIncentiveDetail from './pages/admin/IncentiveDetail.jsx';
import AdminRefunds from './pages/admin/Refunds.jsx';
import AdminReimbursements from './pages/admin/Reimbursements.jsx';
import AdminSettings from './pages/admin/Settings.jsx';
import AdminAoiCalculator from './pages/admin/AoiCalculator.jsx';
import AdminAoiLocation from './pages/admin/AoiLocation.jsx';
import AdminFormBuilder from './pages/admin/FormBuilder.jsx';
import AdminClients from './pages/admin/Clients.jsx';
import ClientSubmissionDetail from './pages/admin/ClientSubmissionDetail.jsx';
import AdminAchievers from './pages/admin/Achievers.jsx';
import AdminHeroSlides from './pages/admin/HeroSlides.jsx';
import AdminFranchiseManager from './pages/admin/FranchiseManager.jsx';
import AdminAccess from './pages/admin/Access.jsx';
import AdminActivity from './pages/admin/Activity.jsx';
import ActorActivity from './pages/admin/ActorActivity.jsx';

import SalesDashboard from './pages/sales/Dashboard.jsx';
import SalesDownloads from './pages/sales/Downloads.jsx';
import SalesIncentives from './pages/sales/Incentives.jsx';
import SalesReimbursements from './pages/sales/Reimbursements.jsx';
import SalesClientNew from './pages/sales/ClientNew.jsx';
import SalesClientList from './pages/sales/ClientList.jsx';
import SalesLeads from './pages/sales/Leads.jsx';
import SalesTeamLeads from './pages/sales/TeamLeads.jsx';
import SalesMyTeam from './pages/sales/MyTeam.jsx';
import SalesLegal from './pages/sales/Legal.jsx';
import SalesKpiAnalytics from './pages/sales/KpiAnalytics.jsx';
import AgeonPaymentLedger from './pages/admin/AgeonPaymentLedger.jsx';
import AgeonClientsList from './pages/admin/AgeonClientsList.jsx';
import ZohoLeads from './pages/admin/ZohoLeads.jsx';
import AdminKpiAnalytics from './pages/admin/KpiAnalytics.jsx';
import Profile from './pages/Profile.jsx';
import { homePathForUser, landingPageVisible, usesAdminWorkspace } from './utils/workspace';
import { useSettings } from './store/settings';
import RequirePageAccess from './components/RequirePageAccess.jsx';

/* Onboarding gate — if the user hasn't finished onboarding, funnel them
   back to /onboarding/<currentStep>. Bypasses for the seeded admin who
   was created with onboardingStep already `complete` (or missing). */
function NeedsOnboarding(user) {
    return user && user.onboardingStep && user.onboardingStep !== 'complete';
}

function PrivateAdmin({ children }) {
    const user = useAuth(s => s.user);
    const token = useAuth(s => s.token);
    if (!token) return <Navigate to="/login" replace />;
    if (!user) return <Loader />;
    if (NeedsOnboarding(user)) return <Navigate to={routeForStep(user)} replace />;
    if (!usesAdminWorkspace(user)) return <Navigate to={homePathForUser(user)} replace />;
    return children;
}
function PrivateAuth({ children }) {
    const user = useAuth(s => s.user);
    const token = useAuth(s => s.token);
    if (!token) return <Navigate to="/login" replace />;
    if (!user) return <Loader />;
    if (NeedsOnboarding(user)) return <Navigate to={routeForStep(user)} replace />;
    return children;
}

/* Onboarding-only gate — must be signed in but not yet `complete`. */
function PrivateOnboarding({ children }) {
    const user = useAuth(s => s.user);
    const token = useAuth(s => s.token);
    const loc = useLocation();
    if (!token) return <Navigate to="/login" replace />;
    if (!user) return <Loader />;
    if (!NeedsOnboarding(user)) {
        // already complete — kick them to the dashboard
        return <Navigate to={routeForStep(user)} replace />;
    }
    // Funnel between onboarding steps if they're on the wrong one
    const want = routeForStep(user);
    if (loc.pathname !== want && want.startsWith('/onboarding/')) {
        return <Navigate to={want} replace />;
    }
    return children;
}

function LandingGate() {
    const user = useAuth(s => s.user);
    const features = useSettings(s => s.features);
    if (!user) return <Loader />;
    if (!landingPageVisible(user, features)) {
        return <Navigate to={homePathForUser(user)} replace />;
    }
    return <Landing />;
}

function AppFallback() {
    const user = useAuth(s => s.user);
    const token = useAuth(s => s.token);
    const features = useSettings(s => s.features);
    if (!token) return <Navigate to="/login" replace />;
    if (!user) return <Loader />;
    if (landingPageVisible(user, features)) return <Navigate to="/" replace />;
    return <Navigate to={homePathForUser(user)} replace />;
}

export default function App() {
    const loadMe = useAuth(s => s.loadMe);
    useEffect(() => { loadMe(); }, [loadMe]);

    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

            {/* ─── ONBOARDING — auth required but onboardingStep ≠ complete. ─── */}
            <Route path="/onboarding" element={<PrivateOnboarding><OnboardingShell /></PrivateOnboarding>}>
                <Route index element={<Navigate to="verify-email" replace />} />
                <Route path="verify-email" element={<OnboardingVerifyEmail />} />
                <Route path="pending-approval" element={<OnboardingPendingApproval />} />
                <Route path="kra" element={<OnboardingAcceptTerms kind="kra" />} />
                <Route path="kpi" element={<OnboardingAcceptTerms kind="kpi" />} />
                <Route path="training" element={<OnboardingTraining />} />
                <Route path="welcome" element={<OnboardingWelcome />} />
            </Route>

            {/* ─── ROOT — marketing landing (toggle in Settings → Feature flags). ─── */}
            <Route path="/" element={<PrivateAuth><LandingGate /></PrivateAuth>} />
            <Route path="/landing" element={<Navigate to="/" replace />} />

            {/* ─── ADMIN — sidebar layout. URLs stay as /admin/dashboard etc. ─── */}
            <Route path="/admin" element={<PrivateAdmin><AppLayout variant="admin" /></PrivateAdmin>}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="approvals" element={<Approvals />} />
                <Route path="users" element={<Users />} />
                <Route path="users/:id" element={<UserProfile />} />
                <Route path="profile" element={<Profile />} />
                <Route path="permissions" element={<Navigate to="/admin/access" replace />} />
                <Route path="downloads" element={<AdminDownloads />} />
                <Route path="incentives" element={<AdminIncentives />} />
                <Route path="incentives/closures/:id" element={<AdminIncentiveDetail />} />
                <Route path="refunds" element={<AdminRefunds />} />
                <Route path="reimbursements" element={<AdminReimbursements />} />
                <Route path="achievers" element={<AdminAchievers />} />
                <Route path="hero-slides" element={<AdminHeroSlides />} />
                <Route path="franchises" element={<AdminFranchiseManager />} />
                <Route path="access" element={<AdminAccess />} />
                <Route path="activity" element={<AdminActivity />} />
                <Route path="activity/actor/:actorId" element={<ActorActivity />} />
                <Route path="roles" element={<Navigate to="/admin/access" replace />} />
                <Route path="form-builder" element={<AdminFormBuilder />} />
                <Route path="payment-ledger" element={<AgeonPaymentLedger />} />
                <Route path="clients" element={<AgeonClientsList />} />
                <Route path="clients/:id" element={<ClientSubmissionDetail />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="kpi-analytics" element={<RequirePageAccess pageKey="admin.kpi-analytics" variant="admin"><AdminKpiAnalytics /></RequirePageAccess>} />
                <Route path="aoi-calculator" element={<AdminAoiCalculator />} />
                <Route path="aoi-location" element={<AdminAoiLocation />} />
                <Route path="landing" element={<Navigate to="/" replace />} />
                <Route path="zoho-leads" element={<ZohoLeads />} />
            </Route>

            {/* ─── SALES — sidebar layout for internal pages (dashboard, clients, etc.) ─── */}
            <Route path="/sales" element={<PrivateAuth><AppLayout variant="sales" /></PrivateAuth>}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<SalesDashboard />} />
                <Route path="clients/new" element={<SalesClientNew />} />
                <Route path="clients/:id" element={<ClientSubmissionDetail />} />
                <Route path="clients" element={<SalesClientList />} />
                <Route path="leads" element={<SalesLeads />} />
                <Route path="team-leads" element={<SalesTeamLeads />} />
                <Route path="my-team" element={<SalesMyTeam />} />
                <Route path="legal" element={<RequirePageAccess pageKey="sales.legal" variant="sales"><SalesLegal /></RequirePageAccess>} />
                <Route path="kpi-analytics" element={<RequirePageAccess pageKey="sales.kpi-analytics" variant="sales"><SalesKpiAnalytics /></RequirePageAccess>} />
                <Route path="downloads" element={<SalesDownloads />} />
                <Route path="incentives" element={<SalesIncentives />} />
                <Route path="incentives/closures/:id" element={<AdminIncentiveDetail />} />
                <Route path="reimbursements" element={<SalesReimbursements />} />
                <Route path="profile" element={<Profile />} />
                <Route path="landing" element={<Navigate to="/" replace />} />
            </Route>

            <Route path="*" element={<AppFallback />} />
        </Routes>
    );
}
